import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Upload, Download, Save, FileText, Check, X, AlertTriangle, 
  Info, RefreshCw, Search, Filter, Grid, Layers, BookOpen,
  Trash2, Eye, Settings, ArrowRight, Users, Calendar
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { debug } from '../../utils/debug';
import { useStabilizedGrid } from '../../contexts/StabilizedContexts';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { 
  LineSubjectMapping, 
  LineSubjectConfig, 
  SubjectLineAvailability,
  createSubjectAvailabilityMap,
  isSubjectAvailableOnLine
} from '../../types/lineSubject';
import { supabase } from '../../supabase/config';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { applyAvailabilityToClasses } from '../../utils/gridSetup';
import { displayLearningArea } from '../../constants/learningAreas';

// The Timetable Editor reads one row per grid from `line_subject_config`
// (`.eq('grid_id', …).single()`), keyed by this config name. The table's unique
// constraint is (grid_id, config_name), so this is the upsert conflict target.
const CONFIG_NAME = 'main_grid_config';

const LineSubjectConfiguration: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { activeGrid } = useStabilizedGrid();
  const { updateGrid } = useMultiGrid();
  
  // State management
  const [lineSubjectMappings, setLineSubjectMappings] = useState<LineSubjectMapping[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [importPreview, setImportPreview] = useState<LineSubjectMapping[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLine, setFilterLine] = useState('');
  const [filterFaculty, setFilterFaculty] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastImportFile, setLastImportFile] = useState<string>('');

  // Load existing mappings for the active grid. Supabase is the source of truth
  // (it's what the Timetable Editor reads); localStorage is only a fallback for
  // when the row hasn't been fetched yet or the request fails.
  useEffect(() => {
    if (!activeGrid) return;

    const loadFromLocalStorage = () => {
      const saved = localStorage.getItem(`line_subject_config_${activeGrid.id}`);
      if (!saved) return;
      try {
        const config: LineSubjectConfig = JSON.parse(saved);
        setLineSubjectMappings(config.mappings || []);
        setLastImportFile(config.importedFrom || '');
      } catch (error) {
        console.error('Error loading line-subject config from cache:', error);
      }
    };

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('line_subject_config')
          .select('mappings')
          .eq('grid_id', activeGrid.id)
          .eq('config_name', CONFIG_NAME)
          .maybeSingle();

        if (cancelled) return;

        if (error) throw error;

        if (data && Array.isArray(data.mappings) && data.mappings.length > 0) {
          setLineSubjectMappings(data.mappings);
        } else {
          // No saved row yet — fall back to any local draft.
          loadFromLocalStorage();
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading line-subject config from database:', error);
        loadFromLocalStorage();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeGrid]);

  // Create availability map for quick lookups
  const subjectAvailability = useMemo(() => {
    return createSubjectAvailabilityMap(lineSubjectMappings);
  }, [lineSubjectMappings]);

  // Get unique values for filtering
  const uniqueLines = useMemo(() => {
    return [...new Set(lineSubjectMappings.map(m => m.lineId))].sort();
  }, [lineSubjectMappings]);

  const uniqueFaculties = useMemo(() => {
    return [...new Set(lineSubjectMappings.map(m => m.faculty))].sort();
  }, [lineSubjectMappings]);

  // Filter mappings based on search and filters
  const filteredMappings = useMemo(() => {
    return lineSubjectMappings.filter(mapping => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !mapping.classCode.toLowerCase().includes(query) &&
          !mapping.className.toLowerCase().includes(query) &&
          !mapping.faculty.toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      
      // Line filter
      if (filterLine && mapping.lineId !== filterLine) {
        return false;
      }
      
      // Faculty filter
      if (filterFaculty && mapping.faculty !== filterFaculty) {
        return false;
      }
      
      return true;
    });
  }, [lineSubjectMappings, searchQuery, filterLine, filterFaculty]);

  // Group mappings by line for display
  const mappingsByLine = useMemo(() => {
    const grouped: { [lineId: string]: LineSubjectMapping[] } = {};
    filteredMappings.forEach(mapping => {
      if (!grouped[mapping.lineId]) {
        grouped[mapping.lineId] = [];
      }
      grouped[mapping.lineId].push(mapping);
    });
    return grouped;
  }, [filteredMappings]);

  // Statistics
  const stats = useMemo(() => {
    const totalMappings = lineSubjectMappings.length;
    const uniqueSubjects = new Set(lineSubjectMappings.map(m => m.classCode)).size;
    const linesWithSubjects = new Set(lineSubjectMappings.map(m => m.lineId)).size;
    const totalLines = activeGrid?.lines.length || 0;
    
    return {
      totalMappings,
      uniqueSubjects,
      linesWithSubjects,
      totalLines,
      coverage: totalLines > 0 ? Math.round((linesWithSubjects / totalLines) * 100) : 0
    };
  }, [lineSubjectMappings, activeGrid]);

  // Handle CSV import
  const handleCSVImport = useCallback((csvText: string, filename: string) => {
    if (!activeGrid) {
      addToast('No active grid selected', 'error');
      return;
    }

    try {
      const lines = csvText.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Find column indices
      const lineIndex = headers.findIndex(h => h.toLowerCase() === 'line');
      const classCodeIndex = headers.findIndex(h => 
        h.toLowerCase() === 'class code' || h.toLowerCase() === 'classCode'
      );
      const classNameIndex = headers.findIndex(h => 
        h.toLowerCase() === 'class name' || h.toLowerCase() === 'className'
      );
      const facultyIndex = headers.findIndex(h => h.toLowerCase() === 'faculty');

      if (lineIndex === -1 || classCodeIndex === -1 || classNameIndex === -1 || facultyIndex === -1) {
        throw new Error('CSV must contain columns: Line, Class Code, Class Name, Faculty');
      }

      const newMappings: LineSubjectMapping[] = [];
      let skippedRows = 0;

      // Debug: Show available grid lines
      debug('Available grid lines:', activeGrid.lines.map(l => `${l.id} (${l.name})`));
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = line.split(',').map(v => v.trim());
        
        const lineId = values[lineIndex];
        const classCode = values[classCodeIndex];
        const className = values[classNameIndex];
        const faculty = values[facultyIndex];
        
        if (!lineId || !classCode || !className || !faculty) continue;

        // Find matching line in grid - try multiple matching strategies
        let gridLine = activeGrid.lines.find(l => l.id === lineId || l.name === lineId);
        
        // If no exact match, try more specific matching patterns
        if (!gridLine) {
          // Try case-insensitive exact match first
          gridLine = activeGrid.lines.find(l => 
            l.id.toLowerCase() === lineId.toLowerCase() || 
            l.name.toLowerCase() === lineId.toLowerCase()
          );
        }
        
        // If still no match, try partial matches but be more specific
        if (!gridLine) {
          gridLine = activeGrid.lines.find(l => {
            const lowerLineId = lineId.toLowerCase();
            const lowerGridId = l.id.toLowerCase();
            const lowerGridName = l.name.toLowerCase();
            
            // Check if CSV line name starts with grid line ID (e.g., "Line A" matches grid ID "A")
            return lowerLineId.startsWith(`line ${lowerGridId}`) ||
                   lowerLineId === lowerGridId ||
                   // Check if grid line name contains the CSV line name as a word
                   lowerGridName.includes(` ${lowerLineId} `) ||
                   lowerGridName.startsWith(`${lowerLineId} `) ||
                   lowerGridName.endsWith(` ${lowerLineId}`);
          });
        }
        
        if (!gridLine) {
          console.warn(`Line ${lineId} not found in grid. Available lines:`, activeGrid.lines.map(l => `${l.id} (${l.name})`).join(', '));
          skippedRows++;
          continue;
        }

        // Debug logging to see what's being matched
        debug(`CSV line "${lineId}" matched to grid line "${gridLine.id}" (${gridLine.name})`);

        newMappings.push({
          id: `${lineId}-${classCode}`,
          lineId: gridLine.id,
          lineName: gridLine.name,
          classCode,
          className,
          faculty,
          curriculumArea: faculty, // Use faculty as curriculum area for now
        });
      }

      if (newMappings.length === 0) {
        throw new Error('No valid mappings found in CSV');
      }

      setImportPreview(newMappings);
      setLastImportFile(filename);
      setShowPreviewModal(true);

      if (skippedRows > 0) {
        addToast(`Processed ${newMappings.length} valid mappings, skipped ${skippedRows} invalid rows`, 'info');
      }

    } catch (error) {
      addToast(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  }, [activeGrid, addToast]);

  // Confirm import
  const confirmImport = useCallback(() => {
    setLineSubjectMappings(importPreview);
    setShowPreviewModal(false);
    addToast(`Successfully imported ${importPreview.length} line-subject mappings`, 'success');
    
    // Save to both localStorage and Supabase with cross-grid validation
    if (activeGrid) {
      const config: LineSubjectConfig = {
        gridId: activeGrid.id,
        mappings: importPreview,
        lastUpdated: new Date(),
        importedFrom: lastImportFile
      };

      // Save to localStorage
      localStorage.setItem(`line_subject_config_${activeGrid.id}`, JSON.stringify(config));

      // Save to Supabase (non-blocking) with cross-grid conflict detection
      (async () => {
        try {
          // Check for cross-grid subject conflicts
          const { data: existingConfigs, error: fetchError } = await supabase
            .from('line_subject_config')
            .select('*')
            .neq('grid_id', activeGrid.id);

          if (fetchError) throw fetchError;

          // Analyze conflicts
          const conflictingSubjects: string[] = [];
          if (existingConfigs) {
            for (const existingConfig of existingConfigs) {
              const existingMappings: LineSubjectMapping[] = Array.isArray(existingConfig.mappings)
                ? existingConfig.mappings
                : [];
              for (const newMapping of importPreview) {
                for (const existingMapping of existingMappings) {
                  // Check if same subject on same line in different grids
                  if (newMapping.classCode === existingMapping.classCode &&
                      newMapping.lineId === existingMapping.lineId &&
                      existingMapping.gridId !== activeGrid.id) {
                    conflictingSubjects.push(`${newMapping.classCode} on line ${newMapping.lineId}`);
                  }
                }
              }
            }
          }

          if (conflictingSubjects.length > 0) {
            console.warn('⚠️ LineSubjectConfig: Cross-grid conflicts detected:', conflictingSubjects);
            addToast(`Warning: ${conflictingSubjects.length} subject-line conflicts detected across grids`, 'warning');
          }

          const { error } = await supabase
            .from('line_subject_config')
            .upsert(
              {
                grid_id: activeGrid.id,
                config_name: CONFIG_NAME,
                mappings: importPreview,
                updated_at: new Date().toISOString(),
              },
              // Tenant-scoped unique index: (tenant_id, grid_id, config_name).
              { onConflict: 'tenant_id,grid_id,config_name' }
            );

          if (error) throw error;
          debug('📊 LineSubjectConfig: Saved to Supabase successfully with conflict detection');

          // Mirror availability onto the grid's classes (allowedLineIds), the
          // source of truth the Timetable Editor now prefers, so edits made here
          // show up there for grids built by the wizard/import.
          try {
            await updateGrid(activeGrid.id, {
              classes: applyAvailabilityToClasses(activeGrid.classes || [], importPreview),
            });
          } catch (gridError) {
            console.error('Error updating class availability:', gridError);
          }
        } catch (error) {
          console.error('Error saving to Supabase:', error);
          addToast('Warning: Failed to save to database', 'warning');
        }
      })();
    }
  }, [importPreview, activeGrid, lastImportFile, addToast, updateGrid]);

  // Clear all mappings
  const handleClearMappings = useCallback(async () => {
    if (await confirm({
      title: 'Clear all mappings?',
      message: 'Are you sure you want to clear all line-subject mappings?',
      confirmLabel: 'Clear all',
      variant: 'danger',
    })) {
      setLineSubjectMappings([]);
      if (activeGrid) {
        localStorage.removeItem(`line_subject_config_${activeGrid.id}`);
        
        // Also remove from Supabase (non-blocking)
        (async () => {
          try {
            const { error } = await supabase
              .from('line_subject_config')
              .delete()
              .eq('grid_id', activeGrid.id)
              .eq('config_name', CONFIG_NAME);

            if (error) throw error;
            debug('📊 LineSubjectConfig: Cleared from Supabase successfully');

            // Also clear the per-class availability hints on the grid.
            try {
              await updateGrid(activeGrid.id, {
                classes: applyAvailabilityToClasses(activeGrid.classes || [], []),
              });
            } catch (gridError) {
              console.error('Error clearing class availability:', gridError);
            }
          } catch (error) {
            console.error('Error clearing from Supabase:', error);
          }
        })();
      }
      addToast('All mappings cleared', 'info');
    }
  }, [activeGrid, addToast, updateGrid]);

  // Export mappings
  const handleExportMappings = useCallback(() => {
    if (lineSubjectMappings.length === 0) {
      addToast('No mappings to export', 'warning');
      return;
    }

    const header = 'Line,Class Code,Class Name,Faculty\n';
    const rows = lineSubjectMappings.map(m => 
      `${m.lineId},${m.classCode},"${m.className}",${m.faculty}`
    ).join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `line_subject_mappings_${activeGrid?.name || 'export'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [lineSubjectMappings, activeGrid]);

  // Generate sample CSV
  const generateSampleCSV = () => {
    const header = 'Line,Class Code,Class Name,Faculty\n';
    const samples = [
      'A,DIARTMADA,DISCOVER - ARTS - MEDIA ART & DESIGN (MAD),Arts',
      'A,DIARTMUSA,DISCOVER - ARTS - MUSIC - FOUNDATIONS,Arts',
      'A,DIENGENGA,DISCOVER - ENGLISH,English',
      'B,DIMATMATA,DISCOVER - MATHS,Maths',
      'B,DISCIBIOA,DISCOVER - SCIENCE - BIOLOGY,Science'
    ].join('\n');

    const blob = new Blob([header + samples], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'line_subject_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!activeGrid) {
    return (
      <div>
        <PageHeader
          title="Subject Availability"
          description="Set which lines each subject is allowed to run on"
        />
        <Card>
          <div className="p-12 text-center">
            <Grid className="h-12 w-12 text-ink-400 mx-auto mb-4" />
            <h3 className="text-card-title text-ink-900">No Grid Selected</h3>
            <p className="mt-2 text-sm text-ink-500">
              Please select a grid to configure line-subject mappings
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Subject Availability"
        description="Set which lines each subject is allowed to run on"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={generateSampleCSV}
            >
              Sample CSV
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => setShowImportModal(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleExportMappings}
              disabled={lineSubjectMappings.length === 0}
            >
              Export
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <BookOpen className="h-8 w-8 text-navy-500 mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Total Mappings</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{stats.totalMappings}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <Layers className="h-8 w-8 text-ok-fg mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Unique Subjects</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{stats.uniqueSubjects}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <Grid className="h-8 w-8 text-navy-500 mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Lines Configured</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{stats.linesWithSubjects}/{stats.totalLines}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-warn-fg mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Coverage</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{stats.coverage}%</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Import Status */}
        {lastImportFile && (
          <Card>
            <div className="p-4 bg-ok-bg border-l-4 border-ok-border">
              <div className="flex">
                <Check className="h-5 w-5 text-ok-solid mr-2" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-ok-fg">
                    Data Imported Successfully
                  </h3>
                  <p className="text-sm text-ok-fg mt-1">
                    Last imported from: {lastImportFile}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={handleClearMappings}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Search and Filter */}
        <Card>
          <div className="p-4 border-b border-ink-200">
            <h3 className="text-card-title text-ink-900">Search & Filter</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search subjects, codes, or learning area..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                />
              </div>

              <select
                value={filterLine}
                onChange={(e) => setFilterLine(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
              >
                <option value="">All Lines</option>
                {uniqueLines.map(line => (
                  <option key={line} value={line}>
                    Line {line}
                  </option>
                ))}
              </select>

              <select
                value={filterFaculty}
                onChange={(e) => setFilterFaculty(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
              >
                <option value="">All Learning Areas</option>
                {uniqueFaculties.map(faculty => (
                  <option key={faculty} value={faculty}>
                    {displayLearningArea(faculty)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Mappings Display */}
        {lineSubjectMappings.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-ink-100">
                <FileText className="h-6 w-6 text-ink-500" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-ink-900">
                No Line-Subject Mappings
              </h3>
              <p className="mt-1 text-sm text-ink-500">
                Import a CSV file to configure which subjects run on each line.
              </p>
              <div className="mt-6 flex justify-center space-x-3">
                <Button variant="secondary" onClick={generateSampleCSV}>
                  Download Template
                </Button>
                <Button variant="primary" onClick={() => setShowImportModal(true)}>
                  Import CSV
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="p-4 border-b border-ink-200">
              <div className="flex justify-between items-center">
                <h3 className="text-card-title text-ink-900">
                  Line-Subject Mappings ({filteredMappings.length})
                </h3>
                <span className="text-sm text-ink-500">
                  Showing {Object.keys(mappingsByLine).length} lines
                </span>
              </div>
            </div>

            <div className="divide-y divide-ink-200">
              {Object.entries(mappingsByLine).map(([lineId, mappings]) => (
                <div key={lineId} className="p-4">
                  <div className="flex items-center mb-3">
                    <div className="flex-shrink-0 w-12 h-8 bg-navy-100 rounded flex items-center justify-center">
                      <span className="font-mono tabular-nums text-sm font-medium text-navy-800">
                        {lineId}
                      </span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-ink-900">
                        Line {lineId} ({mappings.length} subjects)
                      </h4>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {mappings.map((mapping) => (
                      <div
                        key={mapping.id}
                        className="bg-ink-50 rounded-lg p-3 hover:bg-ink-100 transition-colors"
                      >
                        <div className="font-mono tabular-nums text-xs font-medium text-navy-600 mb-1">
                          {mapping.classCode}
                        </div>
                        <div className="text-sm font-medium text-ink-900 mb-1">
                          {mapping.className}
                        </div>
                        <div className="text-xs text-ink-500 mb-1">
                          {displayLearningArea(mapping.faculty)}
                        </div>
                        <div className="text-xs text-navy-500">
                          {(mapping as any).gridName || 'Unknown Grid'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Line-Subject Mappings"
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={generateSampleCSV}
            >
              Download Template
            </Button>
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-navy-50 p-4 rounded-md">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-navy-800">CSV Format</h3>
                <p className="text-sm text-navy-700 mt-1">
                  Your CSV file should include: Line, Class Code, Class Name, Faculty
                </p>
              </div>
            </div>
          </div>

          <div className="border-2 border-dashed border-ink-300 rounded-md p-6">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-ink-400" />
              <div className="mt-2">
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-ink-700">
                    Upload CSV file
                  </span>
                  <input
                    id="csv-upload"
                    name="csv-upload"
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          const text = e.target?.result as string;
                          if (text) {
                            handleCSVImport(text, file.name);
                          }
                        };
                        reader.readAsText(file);
                      }
                    }}
                  />
                </label>
                <p className="mt-1 text-xs text-ink-500">
                  Click to select a CSV file
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Import Preview"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={<Check className="h-4 w-4" />}
              onClick={confirmImport}
            >
              Import {importPreview.length} Mappings
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-ok-bg p-4 rounded-md">
            <div className="flex">
              <Check className="h-5 w-5 text-ok-solid mr-2" />
              <div>
                <h3 className="text-sm font-medium text-ok-fg">
                  Ready to Import
                </h3>
                <p className="text-sm text-ok-fg mt-1">
                  Found {importPreview.length} valid line-subject mappings
                </p>
              </div>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-ink-200 rounded-md">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Line</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Subject</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Learning Area</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {importPreview.slice(0, 20).map((mapping, index) => (
                  <tr key={index}>
                    <td className="font-mono tabular-nums px-4 py-2 text-sm font-medium text-ink-900">{mapping.lineId}</td>
                    <td className="font-mono tabular-nums px-4 py-2 text-sm text-ink-900">{mapping.classCode}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{mapping.className}</td>
                    <td className="px-4 py-2 text-sm text-ink-500">{mapping.faculty}</td>
                  </tr>
                ))}
                {importPreview.length > 20 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-2 text-sm text-ink-500 text-center">
                      ... and {importPreview.length - 20} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LineSubjectConfiguration;